import { CSVFile } from "pages/Datasets/types";
import formatBytes from "utils/formatBytes";
import { isBlank } from "utils/string";
import { produce } from "immer";
import random from "utils/random";
import {
  Box,
  FormHelperText,
  Popover,
  SvgIcon,
  Tooltip,
} from "@material-ui/core";
import TextFileInput from "../TextFileInput";
import ButtonWithLoading from "components/ButtonWithLoading";
import { useTranslation } from "react-i18next";
import { KeyValueItem, State } from "./types";
import validateItem from "./validateItem";
import { useSnackbar } from "notistack";
import { InfoOutlined } from "@material-ui/icons";
import { useState } from "react";

type Props = {
  state: State;
  onChange: (updatedState: State) => void;
};

type Counter = {
  errors: number;
  total: number;
  omited: number;
  append: number;
  updated: number;
};

const limitInMb = 2;

export default function UploadFileBtn({ state, onChange }: Props) {
  const { t } = useTranslation("datasets");
  const { enqueueSnackbar } = useSnackbar();

  const handleFileUpload = (file: CSVFile, mode: "update" | "replace_all") => {
    const { content, size } = file;
    const COMMA_SEPARATED = /.+,.+/g; // This allows special characters and spaces

    if (isBlank(content) || !COMMA_SEPARATED.test(content)) {
      return t("form.fields.datasetFile.errors.content");
    }
    if (size >= 1024 * 1024 * limitInMb) {
      return t("form.fields.datasetFile.errors.size", {
        size: formatBytes(size),
      });
    }

    if (mode === "update") {
      updateFromFile(content);
    } else {
      replaceAllFromFile(content);
    }
  };

  function replaceAllFromFile(content: string) {
    const updatedState = produce(state, draft => {
      draft.items = [];
      draft.errors = {};
      draft.touched = {};

      content.split("\n").forEach(set => {
        const values = set.split(",");

        if (values.length < 2) return;

        const key = values[0].trim();
        const value = values[1].trim();

        if (!key) return;

        const item: KeyValueItem = { id: random(), key, value };
        const error = validateItem(state, item);

        if (error) {
          draft.errors[item.id] = error;
        } else {
          delete draft.errors[item.id];
          draft.items.unshift(item);
        }
        draft.touched[item.id] = { key: true, value: true };
      });
    });

    onChange(updatedState);
  }

  function updateFromFile(content: string) {
    let anyChange = false;
    let emptyRowsRemoved = false;
    let count: Counter = {
      errors: 0,
      total: 0,
      omited: 0,
      append: 0,
      updated: 0,
    };

    const updatedState = produce(state, draft => {
      // Remove empty placeholder rows (e.g. the initial empty row on mount or
      // rows added via "Add row" that were never filled in). Without this, their
      // errors entries persist and block the Save button after a successful import.
      const emptyRows = draft.items.filter(item => !item.key && !item.value);
      if (emptyRows.length > 0) {
        emptyRowsRemoved = true;
        emptyRows.forEach(item => {
          delete draft.errors[item.id];
          delete draft.touched[item.id];
        });
        draft.items = draft.items.filter(item => item.key || item.value);
      }

      content.split("\n").forEach(set => {
        const values = set.split(",");

        if (values.length < 2) return;

        const key = values[0].trim();
        const value = values[1].trim();

        count.total += 1;

        if (!key || keyAndValueExist(key, value, draft.items)) {
          count.omited += 1;
          return;
        }

        const item: KeyValueItem = { id: random(), key, value };
        const error = validateItem(state, item);

        if (error) {
          count.errors += 1;
          draft.errors[item.id] = error;
        } else {
          delete draft.errors[item.id];
          anyChange = true;

          const index = draft.items.findIndex(
            current => current.key === item.key
          );

          if (index >= 0) {
            count.updated += 1;
            const existingId = draft.items[index].id;
            draft.items[index] = item;
            delete draft.touched[existingId];
          } else {
            count.append += 1;
            draft.items.unshift(item);
          }
        }
        draft.touched[item.id] = { key: true, value: true };
      });
    });

    if (anyChange) {
      enqueueSnackbar(<Summary count={count} />, { variant: "info" });
    } else {
      enqueueSnackbar(
        t("importResult.withoutChanges", { count: count.total }),
        { variant: "info" }
      );
    }

    if (anyChange || emptyRowsRemoved) {
      onChange(updatedState);
    }
  }

  function Summary({ count }: { count: Counter }) {
    const { t } = useTranslation("datasets");
    return (
      <div>
        <div>{t("importResult.title", { count: count.total })}</div>
        <ul>
          {count.omited > 0 && (
            <li>{t("importResult.omited", { count: count.omited })}</li>
          )}
          <li>
            {count.append > 0
              ? t("importResult.append", { count: count.append })
              : t("importResult.noItemsAdded")}
          </li>
          <li>
            {count.updated > 0
              ? t("importResult.updated", { count: count.updated })
              : t("importResult.noUpdates")}
          </li>
          {count.errors > 0 && (
            <li>{t("importResult.errors", { count: count.errors })}</li>
          )}
        </ul>
        <Box mt={2}>
          <strong>{t("importResult.actionLabel")}</strong>
        </Box>
      </div>
    );
  }

  function keyAndValueExist(key: string, value: string, items: KeyValueItem[]) {
    return items.some(item => item.key === key && item.value === value);
  }

  return (
    <Box display="flex" alignItems="center">
      <Box mr="8px" ml="20px" display="flex" alignItems="center" gridGap={1}>
        <CsvIcon />
        {t("import.title")}
      </Box>
      <Box display="flex" gridGap=".5rem" alignItems="center">
        <UploadBtn
          name="update-file-input"
          tooltip={t("import.updateDesc")}
          label={t("import.update")}
          buttonTestId="dataset-form-upload-update-btn"
          fileInputTestId="dataset-form-file-input-update"
          onFileUpload={file => {
            handleFileUpload(file, "update");
          }}
        />

        <UploadBtn
          name="replace-file-input"
          tooltip={t("import.replaceDesc")}
          label={t("import.replace")}
          buttonTestId="dataset-form-upload-replace-btn"
          fileInputTestId="dataset-form-file-input-replace"
          onFileUpload={file => handleFileUpload(file, "replace_all")}
        />
      </Box>
      <Info />
    </Box>
  );
}

type UploadBtnProps = {
  name: string;
  label: string;
  tooltip: string;
  buttonTestId: string;
  fileInputTestId: string;
  onFileUpload: (file: CSVFile) => void;
};

function UploadBtn({
  name,
  label,
  tooltip,
  buttonTestId,
  fileInputTestId,
  onFileUpload,
}: UploadBtnProps) {
  return (
    <>
      <Tooltip title={tooltip} aria-label={tooltip} arrow>
        <span>
          <TextFileInput
            name={name}
            accept=".csv"
            data-testid={fileInputTestId}
            onFileUpload={onFileUpload}
            renderAs={({ isUploading, uploadError }) => (
              <>
                <ButtonWithLoading
                  data-testid={buttonTestId}
                  variant="outlined"
                  color="primary"
                  size="small"
                  loading={isUploading}
                  onClick={() => {}}
                  component="span"
                >
                  {label}
                </ButtonWithLoading>
                {Boolean(uploadError) && (
                  <FormHelperText error>{uploadError}</FormHelperText>
                )}
              </>
            )}
          />
        </span>
      </Tooltip>
    </>
  );
}

function Info() {
  const { t } = useTranslation("datasets");
  const [anchorEl, setAnchorEl] = useState(null);

  /*
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  */

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePopoverOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = "dataset-file-info-popover";

  return (
    <Box>
      <Box
        display="flex"
        pl="8px"
        color="gray"
        aria-haspopup="true"
        aria-owns={open ? id : undefined}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        data-testid="dataset-form-upload-info-btn"
      >
        <InfoOutlined />
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        style={{ pointerEvents: "none" }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        disableRestoreFocus
      >
        <Box p="15px">
          <Box mb="15px">{t("import.help.title")}</Box>
          <ul>
            <li>
              <strong>{t("import.update")}:</strong> {t("import.updateDesc")}
            </li>
            <li>
              <strong>{t("import.replace")}:</strong> {t("import.replaceDesc")}
            </li>
          </ul>

          <Box mt="30px">{t("import.fileRules")}</Box>
          <ul>
            <li>{t("import.sizeLimit", { size: limitInMb })}</li>
            <li>{t("form.tooltip.datasetFile.format")}</li>
            <li>{t("form.tooltip.datasetFile.columns")}</li>
          </ul>
        </Box>
      </Popover>
    </Box>
  );
}

function CsvIcon() {
  return (
    <SvgIcon>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="icon icon-tabler icons-tabler-outline icon-tabler-file-type-csv"
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M14 3v4a1 1 0 0 0 1 1h4" />
        <path d="M5 12v-7a2 2 0 0 1 2 -2h7l5 5v4" />
        <path d="M7 16.5a1.5 1.5 0 0 0 -3 0v3a1.5 1.5 0 0 0 3 0" />
        <path d="M10 20.25c0 .414 .336 .75 .75 .75h1.25a1 1 0 0 0 1 -1v-1a1 1 0 0 0 -1 -1h-1a1 1 0 0 1 -1 -1v-1a1 1 0 0 1 1 -1h1.25a.75 .75 0 0 1 .75 .75" />
        <path d="M16 15l2 6l2 -6" />
      </svg>
    </SvgIcon>
  );
}
